import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer, zip } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as fromGenerated from '../../_generated';
import { LayoutActions } from '../../layout/store/layout.actions';
import { LOADING_TIME_FOR_MESSAGE } from '../chats.constants';
import { ChatsSocket } from '../chats.socket';
import { ChatActions, ChatsActions, ChatsSocketActions, ChatWithMessagesActions } from './chats.actions';
import { chatsFeature, ChatsState } from './chats.reducer';

@Injectable()
export class ChatsEffects {
  private readonly actions$ = inject(Actions);
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly store = inject(Store<ChatsState>);
  private readonly router = inject(Router);
  private readonly chatsSocket = inject(ChatsSocket);
  private readonly CONVERSATION_URL = '/request';

  unselectChatOnNavigation$ = createEffect(() =>
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter((event) => {
        const previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString() ?? '';
        return previousUrl.includes(this.CONVERSATION_URL) && !event.url.includes(this.CONVERSATION_URL);
      }),
      switchMap(() => [ChatActions.unselectChat()]),
    ),
  );

  loadChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChats),
      exhaustMap(() =>
        this.chatsService.chatsControllerGetChats().pipe(
          map((chats) => ChatsActions.loadChatsSuccess({ chats })),
          catchError((error: Error) => of(ChatsActions.loadChatsFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  loadChatsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChatsSuccess),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChatId)),
      map(([{ chats }, selectedChatId]) =>
        ChatActions.selectChat({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: selectedChatId ?? chats.find((chat) => !chat._count.messages)!.id,
        }),
      ),
    ),
  );

  selectChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.selectChat),
      switchMap(({ id, navigateToRequest }) => {
        const currentUrl = this.router.url;
        // Navigate to /request if explicitly requested or if we're on the home page
        if (navigateToRequest ?? (currentUrl === '/' || currentUrl === '')) {
          void this.router.navigate(['/request']);
        }
        return [LayoutActions.setRightPanelOpened({ isOpened: false }), ChatWithMessagesActions.loadChatWithMessages({ id })];
      }),
    ),
  );

  selectEmptyChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.selectEmptyChat),
      switchMap(() => [ChatActions.loadEmptyChat(), LayoutActions.setRightPanelOpened({ isOpened: false })]),
    ),
  );

  unselectChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.unselectChat),
      map(() => LayoutActions.setRightPanelOpened({ isOpened: false })),
    ),
  );

  loadEmptyChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadEmptyChat),
      withLatestFrom(this.store.select(chatsFeature.selectAll)),
      switchMap(([, chats]) => {
        const emptyChat = chats.find((chat) => chat && chat._count.messages === 0);

        if (!emptyChat) {
          return this.chatsService.chatsControllerGetChats().pipe(
            map(
              (chats) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                chats.find((chat) => !chat._count.messages)!,
            ),
          );
        }
        return of(emptyChat);
      }),
      map((chat) =>
        ChatActions.loadEmptyChatSuccess({
          chat,
        }),
      ),
      catchError((error: Error) => of(ChatActions.loadEmptyChatFailure({ error: error.message }))),
    ),
  );

  loadChatWithMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.loadChatWithMessages),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChat)),
      switchMap(([{ id }, chat]) => {
        /**
         * If the chat is empty, we don't need to load the messages
         */
        if (chat && chat._count.messages === 0) {
          return of(
            ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages: { ...chat, messages: chat.messages ?? [] },
            }),
          );
        }

        return zip(this.chatsService.chatsControllerGetChatWithMessages(id), timer(LOADING_TIME_FOR_MESSAGE)).pipe(
          map(([chatWithMessages]) => {
            return ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages,
            });
          }),
          catchError((error: Error) =>
            of(
              ChatWithMessagesActions.loadChatWithMessagesFailure({
                error: error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  addChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.addChatMessage),
      switchMap(({ chatId, content }) => {
        const addChatMessageDto: fromGenerated.AddChatMessageDto = {
          content,
        };

        return this.chatsService.chatsControllerAddChatMessage(chatId, addChatMessageDto).pipe(
          switchMap(() => [ChatWithMessagesActions.addChatMessageSuccess()]),
          catchError((error: Error) =>
            of(
              ChatWithMessagesActions.addChatMessageFailure({
                chatId,
                error: error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  deleteChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.deleteChats),
      switchMap(() =>
        this.chatsService.chatsControllerDeleteChats().pipe(
          map(() => ChatsActions.deleteChatsSuccess()),
          catchError((error: Error) => of(ChatsActions.deleteChatsFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  deleteChatsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.deleteChatsSuccess),
      map(() => ChatActions.loadEmptyChat()),
    ),
  );

  deleteChatSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.deleteChatSuccess),
      map(() => ChatActions.loadEmptyChat()),
    ),
  );

  chatsSocketConnect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsSocketActions.reconnect),
        tap(() => {
          this.chatsSocket.socket.disconnect();
          this.chatsSocket.socket.connect();
          this.chatsSocket.socket.on('connect_error', (err) => {
            console.error(err);
          });
        }),
      ),
    { dispatch: false },
  );
}
